import { EqxLoading } from '@equinoxx/react'
import React from 'react'
import appApi from '../../classes/appApi'
import { PageErrorNoRouting } from '../../pages/PageError'
import { useBaseConfigContext } from './AppBaseConfig'
import { useTenantID } from './AppTenantID'

export function buildTenantUrlPrefix(displayID: string, instance?: string | null): string {
    const encoded = encodeURIComponent(displayID)
    let prefix = `_${encoded}`
    if (instance) {
        prefix += `-${instance}`
    }
    return prefix
}

interface TenantLookupContext {
    tenantUrlPrefix: string
    displayID: string
}

const tenantLookupContext = React.createContext<TenantLookupContext | undefined>(undefined)

export const useTenantLookupContext = () => {
    const context = React.useContext(tenantLookupContext)
    if (context === undefined) {
        throw new Error('useTenantLookupContext must be used within AppTenantLookupProvider')
    }
    return context
}

const AppTenantLookupProvider = (props: { children: React.ReactNode }) => {
    const tenantID = useTenantID()
    const config = useBaseConfigContext()
    const [tenantUrlPrefix, setTenantUrlPrefix] = React.useState<string | null>(null)
    const [displayID, setDisplayID] = React.useState<string | null>(null)
    const [error, setError] = React.useState<string | undefined>(undefined)

    React.useEffect(() => {
        let cancelled = false
        const api = new appApi({ config: config.api })
        api.endpoints
            .tenantById(tenantID.toUpperCase())
            .execute(api)
            .then((tenant) => {
                if (cancelled) return
                const resolvedDisplayID = tenant.displayID?.trim()
                if (!resolvedDisplayID) {
                    setError('No display ID returned for tenant')
                    return
                }
                setDisplayID(resolvedDisplayID)
                setTenantUrlPrefix(buildTenantUrlPrefix(resolvedDisplayID))
            })
            .catch(() => {
                if (!cancelled) {
                    setError('Error resolving tenant')
                }
            })
        return () => {
            cancelled = true
        }
    }, [tenantID, config.api])

    if (error) {
        return <PageErrorNoRouting error={error} />
    }
    if (!tenantUrlPrefix || !displayID) {
        return <EqxLoading />
    }

    return (
        <tenantLookupContext.Provider value={{ tenantUrlPrefix, displayID }}>
            {props.children}
        </tenantLookupContext.Provider>
    )
}

export default AppTenantLookupProvider
